/**
 * The lobby room, client side.
 *
 * ── Two transports, one for each direction ───────────────────────────────────
 *
 * Messages arrive on a socket (`/chat`, on the same `/ws` path the arena uses,
 * on its own namespace) and are sent over HTTP. That split is the server's
 * design and this file just honours it: receiving needs a push, sending needs
 * guards, a throttle and payload validation, and all of those already exist on
 * the HTTP path. See `backend/src/game/chat/chat.controller.ts`.
 *
 * ── Module scope, not per-component ──────────────────────────────────────────
 *
 * The same pattern `state/settings.ts` uses. The panel is mounted and unmounted
 * as the garage's tabs change; a room that reconnected and refetched every time
 * would drop lines a player was reading and hammer the socket. The connection
 * is opened once, on the first subscriber, and lives as long as the app does.
 *
 * ── A room that will not connect is a quiet room, never an error ─────────────
 *
 * A refused socket falls back to polling the same history endpoint, and a
 * refused poll leaves the panel with whatever it already had. Chat is not
 * load-bearing: nobody should be unable to reach the garage because a
 * WebSocket was blocked by a corporate proxy. `chatDiagnostics()` answers what
 * actually happened, the same shape `immersiveDiagnostics()` uses and for the
 * same reason — "it is empty" is otherwise unanswerable without guessing.
 */
import { computed, ref } from 'vue';
import { io, type Socket } from 'socket.io-client';

import { api, ApiError, type ChatMessage } from '@/net/api';

/** Mirrors the server's own limit, so the box stops rather than the request failing. */
export const CHAT_MAX_LENGTH = 200;

/** How often to poll when the socket is unavailable. */
const POLL_MS = 6_000;

const messages = ref<ChatMessage[]>([]);
const connected = ref(false);
const sending = ref(false);
const error = ref<string | null>(null);

let socket: Socket | null = null;
let poll: number | null = null;
let started = false;
let socketFailures = 0;
let polls = 0;

/** Keeps the buffer bounded even if the server's own limit is ever raised. */
const KEEP = 80;

function absorb(incoming: ChatMessage[]): void {
  // Merged by id rather than replaced, so a poll landing beside a socket
  // message cannot duplicate a line or lose one that arrived in between.
  const byId = new Map(messages.value.map((m) => [m.id, m]));
  for (const message of incoming) byId.set(message.id, message);
  messages.value = [...byId.values()].sort((a, b) => a.at - b.at).slice(-KEEP);
}

function startPolling(): void {
  if (poll !== null) return;
  poll = window.setInterval(() => {
    polls += 1;
    api
      .chatHistory()
      .then((r) => absorb(r.messages))
      .catch(() => {});
  }, POLL_MS);
}

function stopPolling(): void {
  if (poll !== null) window.clearInterval(poll);
  poll = null;
}

function connect(): void {
  // Same origin: in dev Vite proxies `/ws` through to the API, exactly as the
  // arena connection relies on.
  socket = io('/chat', { path: '/ws', transports: ['websocket'], withCredentials: true });

  socket.on('connect', () => {
    connected.value = true;
    stopPolling();
  });
  socket.on('chat:history', (payload: ChatMessage[]) => absorb(payload));
  socket.on('chat:message', (payload: ChatMessage) => absorb([payload]));
  socket.on('disconnect', () => {
    connected.value = false;
    // The server disconnects a socket whose cookie will not verify, and
    // socket.io will retry that forever. Polling covers the gap either way.
    startPolling();
  });
  socket.on('connect_error', () => {
    socketFailures += 1;
    connected.value = false;
    startPolling();
  });
}

/** Opens the room. Safe to call from every mounted panel; only the first acts. */
export function startChat(): void {
  if (started) return;
  started = true;
  // Seeded over HTTP as well as by the socket's own `chat:history`, so the
  // panel has content on the first frame rather than one round trip later.
  api
    .chatHistory()
    .then((r) => absorb(r.messages))
    .catch(() => {});
  connect();
}

/**
 * Says something.
 *
 * The sent message is **not** added locally — it arrives back over the socket
 * like everybody else's. Echoing it optimistically would put it on screen
 * twice for anyone whose socket is up, and would show a line the server may
 * have refused. The cooldown makes the round trip cheap to wait for.
 */
export async function say(body: string): Promise<boolean> {
  const text = body.trim();
  if (text.length === 0 || sending.value) return false;
  sending.value = true;
  error.value = null;
  try {
    const sent = await api.chatSay(text.slice(0, CHAT_MAX_LENGTH));
    // Absorbed anyway for the polling case, where the round trip is the only
    // way this line appears for up to `POLL_MS`. The id-keyed merge means the
    // socket's copy of the same message is not a second row.
    absorb([sent]);
    return true;
  } catch (caught) {
    error.value =
      caught instanceof ApiError ? caught.message : 'Could not send that. Try again.';
    return false;
  } finally {
    sending.value = false;
  }
}

export function useChat() {
  return {
    messages: computed(() => messages.value),
    connected: computed(() => connected.value),
    sending: computed(() => sending.value),
    error: computed(() => error.value),
    clearError: () => {
      error.value = null;
    },
  };
}

/** Why the room is the way it is. Read by hand; nothing branches on it. */
export function chatDiagnostics(): {
  started: boolean;
  connected: boolean;
  socketFailures: number;
  polls: number;
  held: number;
} {
  return {
    started,
    connected: connected.value,
    socketFailures,
    polls,
    held: messages.value.length,
  };
}
